import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlContainer, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { TranslatePipe } from '@ngx-translate/core';
import { PersonOption, PersonSelectComponent } from '../../../components/person-select/person-select.component';
import { PersonMultiSelectComponent } from '../../../components/person-multi-select/person-multi-select.component';
import { TEST_CASE_PRIORITIES } from '../../../models/test-case.model';
import {
  TestCaseSelectionList,
  TestCaseSelectionListKey,
  TestCaseSelectionListService,
} from '../../../services/test-case-selection-list.service';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';

export type EigenschaftenAuswahlControl = 'type' | 'testType' | 'status' | 'automationStatus';

interface AuswahlFeld {
  control: EigenschaftenAuswahlControl;
  listKey: TestCaseSelectionListKey;
  testid: string;
  labelKey: string;
  requiredKey: string | null;
}

interface Option {
  key: string;
  label: string;
}

const AUSWAHL_FELDER: readonly AuswahlFeld[] = [
  {
    control: 'type',
    listKey: 'test-case-type',
    testid: 'typ',
    labelKey: 'testCases.form.properties.type',
    requiredKey: 'testCases.form.properties.typeRequired',
  },
  {
    control: 'testType',
    listKey: 'test-case-test-type',
    testid: 'testart',
    labelKey: 'testCases.form.properties.testType',
    requiredKey: 'testCases.form.properties.testTypeRequired',
  },
  {
    control: 'status',
    listKey: 'test-case-status',
    testid: 'status',
    labelKey: 'testCases.form.properties.status',
    requiredKey: 'testCases.form.properties.statusRequired',
  },
  {
    control: 'automationStatus',
    listKey: 'test-case-automation-status',
    testid: 'automatisierung',
    labelKey: 'testCases.form.properties.automationStatus',
    requiredKey: null,
  },
];

export const AUSWAHLLISTE_ZU_CONTROL = Object.fromEntries(
  AUSWAHL_FELDER.map((feld) => [feld.listKey, feld.control]),
) as Record<TestCaseSelectionListKey, EigenschaftenAuswahlControl>;

type Ladezustand = 'laedt' | 'fehler' | 'geladen';

function fehlerVon(
  control: AbstractControl | null,
  requiredKey: string | null,
): { key?: string; text?: string } | null {
  if (!control?.touched) return null;
  const server = control.getError('server') as string | null;
  if (server) return { text: server };
  return requiredKey && control.hasError('required') ? { key: requiredKey } : null;
}

@Component({
  selector: 'app-test-case-eigenschaften',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatIcon,
    MatChipGrid,
    MatChipRow,
    MatChipInput,
    MatChipRemove,
    PersonSelectComponent,
    PersonMultiSelectComponent,
  ],
  // Die Felder hängen direkt am Formular der Seite: Fokus auf das erste Fehlerfeld und Sperre für Leser greifen so ohne Umweg.
  viewProviders: [{ provide: ControlContainer, useFactory: () => inject(ControlContainer, { skipSelf: true }) }],
  templateUrl: './test-case-eigenschaften.component.html',
  styleUrl: './test-case-eigenschaften.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseEigenschaftenComponent implements OnInit {
  private readonly container = inject(ControlContainer);
  private readonly service = inject(TestCaseSelectionListService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly readOnly = input(false);
  readonly personOptions = input<PersonOption[]>([]);
  readonly version = input<number | null>(null);
  readonly listenGeladen = output<TestCaseSelectionList[]>();

  readonly priorityOptions = TEST_CASE_PRIORITIES;
  readonly fieldLengths = FIELD_LENGTHS;
  readonly jiraTicketFields: readonly {
    controlName: 'storyJiraKey' | 'bugJiraKey';
    labelKey: string;
    placeholderKey: string;
  }[] = [
    {
      controlName: 'storyJiraKey',
      labelKey: 'testCases.form.properties.storyTicket',
      placeholderKey: 'testCases.form.properties.storyTicketPlaceholder',
    },
    {
      controlName: 'bugJiraKey',
      labelKey: 'testCases.form.properties.bugTicket',
      placeholderKey: 'testCases.form.properties.bugTicketPlaceholder',
    },
  ];

  readonly zustand = signal<Ladezustand>('laedt');
  private readonly listen = signal<TestCaseSelectionList[]>([]);
  private readonly formStand = signal(0);

  readonly auswahlFelder = computed(() => {
    this.formStand();
    const listen = this.listen();
    return AUSWAHL_FELDER.map((feld) => {
      const control = this.form.get(feld.control);
      const wert = (control?.value as string | null) ?? '';
      const werte = listen.find((l) => l.listKey === feld.listKey)?.werte ?? [];
      const optionen: Option[] = werte
        .filter((w) => w.active)
        .map((w) => ({ key: w.key, label: this.service.beschriftung(feld.listKey, w) }));
      const inaktiv: Option | null =
        wert && !optionen.some((o) => o.key === wert)
          ? { key: wert, label: this.service.beschriftungZuSchluessel(feld.listKey, wert, werte) }
          : null;
      return {
        ...feld,
        id: `tf-eigenschaften-${feld.testid}-feld`,
        fehlerId: `tf-eigenschaften-${feld.testid}-fehler`,
        hinweisId: `tf-eigenschaften-${feld.testid}-hinweis`,
        optionen,
        inaktiv,
        aktuell: this.service.beschriftungZuSchluessel(feld.listKey, wert, []),
        fehler: fehlerVon(control, feld.requiredKey),
      };
    });
  });

  readonly verantwortlicherFehler = computed(() => {
    this.formStand();
    return fehlerVon(this.form.get('assigneeUserId'), 'testCases.form.properties.assigneeRequired');
  });

  readonly tags = computed(() => {
    this.formStand();
    return (this.form.get('tags')?.value as string[] | null) ?? [];
  });

  get form(): FormGroup {
    return this.container.control as FormGroup;
  }

  ngOnInit(): void {
    this.form.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.formStand.update((n) => n + 1));
    this.laden();
  }

  laden(): void {
    this.zustand.set('laedt');
    this.service
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (listen) => {
          this.listen.set(listen);
          this.zustand.set('geladen');
          this.listenGeladen.emit(listen);
        },
        error: () => this.zustand.set('fehler'),
      });
  }

  addTag(event: MatChipInputEvent): void {
    if (this.readOnly()) return;
    const value = (event.value ?? '').trim();
    const control = this.form.get('tags');
    const tags = this.tags();
    if (control && value && !tags.includes(value)) {
      control.setValue([...tags, value]);
      control.markAsDirty();
    }
    event.chipInput.clear();
  }

  removeTag(tag: string): void {
    if (this.readOnly()) return;
    const control = this.form.get('tags');
    if (!control) return;
    control.setValue(this.tags().filter((t) => t !== tag));
    control.markAsDirty();
  }
}
